/**
 * monetization/providers — provider-agnostic payment abstraction (client side).
 *
 * The client NEVER talks to the gateway directly: the server exposes
 * POST /api/billing/checkout and the provider injected there decides (Rust
 * `PaymentProvider` trait). This TS mirror describes how the UI should
 * INTERPRET the active provider: whether it offers automatic checkout or the
 * upgrade is a manual flow (provider "none": an admin applies it).
 *
 * Adding a new provider = register its descriptor here + implement the trait
 * in Rust. No screen or domain logic changes.
 */

import {
  clampProviderToPlatform,
  isStoreBinaryPlatform,
  normalizePlatform,
  type PurchaseGateReason,
  type ResolvedPurchasePlatform,
  UNKNOWN_PURCHASE_PLATFORM,
} from "./purchase";

export interface CheckoutResult {
  provider: string;
  /** Hosted payment URL to redirect to (if the provider offers one). */
  checkoutUrl: string | null;
  /** true ⇒ manual upgrade (contact support / an admin applies it). */
  manual: boolean;
}

/** Payment provider descriptor as seen by the UI. */
export interface PaymentProviderDescriptor {
  id: string;
  /** Does the upgrade open a hosted checkout? (false ⇒ manual flow). */
  supportsCheckout: boolean;
  /** Does the server receive webhooks from this provider? */
  supportsWebhooks: boolean;
}

const NONE_PROVIDER: PaymentProviderDescriptor = {
  id: "none",
  supportsCheckout: false,
  supportsWebhooks: false,
};

const REGISTRY: Record<string, PaymentProviderDescriptor> = {
  none: NONE_PROVIDER,
  stripe: { id: "stripe", supportsCheckout: true, supportsWebhooks: true },
};

/** Registered IDs (for the admin panel Select). */
export const PAYMENT_PROVIDER_IDS = Object.keys(REGISTRY);

/** Active provider descriptor; unknown ⇒ behaves like "none". */
export function getPaymentProvider(id: string | undefined): PaymentProviderDescriptor {
  return REGISTRY[id ?? "none"] ?? NONE_PROVIDER;
}

/**
 * Interprets the server's checkout response in a provider-agnostic way:
 * returns the action the UI must perform.
 *
 * `blocked` ⇒ **navigate nowhere**: the surface does not support that
 * purchase (see `monetization/purchase.ts`).
 */
export type CheckoutAction =
  | { kind: "redirect"; url: string }
  | { kind: "manual" }
  | { kind: "blocked"; reason: PurchaseGateReason };

/**
 * **The client's second gate, and the one that truly closes.**
 *
 * `platform` is deliberately a REQUIRED parameter. The previous version of
 * this function did not have it and returned `redirect` for any URL arriving
 * from the server; the plan screen's `location.assign` did the rest. Making it
 * required turns an omission into a **type error** in every clone: there is no
 * need to remember the guard because it cannot compile without one. (It is
 * deliberately a type rather than a lint: in this fleet, text-only guards did
 * not guard anything.)
 *
 * Rules, all fail-closed:
 *   * unclassified surface ⇒ `blocked`;
 *   * provider returned by the server that is illegal on this surface
 *     (Stripe in the APK) ⇒ `blocked`;
 *   * **store binary ⇒ never navigate to a payment URL**, even with a legal
 *     provider: Play's policy also targets *link-out*;
 *   * no URL or a manual flow ⇒ `manual`, as always.
 */
export function interpretCheckout(
  result: CheckoutResult,
  platform: ResolvedPurchasePlatform | string,
): CheckoutAction {
  const surface = normalizePlatform(platform);
  if (surface === UNKNOWN_PURCHASE_PLATFORM) {
    return { kind: "blocked", reason: "unknown-platform" };
  }
  const provider = result.provider.trim().toLowerCase();
  if (clampProviderToPlatform(surface, provider) !== provider) {
    return { kind: "blocked", reason: "provider-not-allowed" };
  }
  if (!result.manual && result.checkoutUrl) {
    if (isStoreBinaryPlatform(surface)) {
      // Belt and suspenders: even if a provider with a URL ever becomes legal
      // in a store binary, outbound navigation does not.
      return { kind: "blocked", reason: "provider-not-allowed" };
    }
    return { kind: "redirect", url: result.checkoutUrl };
  }
  return { kind: "manual" };
}
